import type { FileInfo } from '@/api/file';
import * as fileApi from '@/api/file';
import type { SharedWithMeEntry } from '@/api/share';
import * as shareApi from '@/api/share';
import { unsecuredCopyToClipboard } from '@/utils/clipboard';
import { fileService, isExcelFile, isThisAFile, isWordFile } from '@/utils/filesService';
import useFileStore, { FileExtension, FileInfoExtended, FileType, findNode } from '@/utils/fileStore';
import { usePermissionsStore } from '@/utils/permissionsStore';
import { FolderAddOutlined, FolderOpenOutlined, HomeOutlined, ReloadOutlined } from '@ant-design/icons';
import { DndContext, DragOverlay, PointerSensor, pointerWithin, useDroppable, useSensor, useSensors, type DragEndEvent, type DragMoveEvent, type DragStartEvent } from '@dnd-kit/core';
import { useClickAway, useKeyPress, useUpdateEffect } from 'ahooks';
import { App, Button, Checkbox, Dropdown, Input, MenuProps, Select, Tooltip, Tree, TreeDataNode } from 'antd';
import { ItemType } from 'antd/es/menu/interface';
import type { TreeProps } from 'antd/es/tree';
import type { UploadFile } from 'antd/es/upload/interface';
import pathbe from 'path-browserify-esm';
import React, { useEffect, useRef, useState } from 'react';
import type { LLMDropItem } from '@/types/llmDrop';
import { v4 as uuidv4 } from 'uuid';
import CreateFileDialog from './createFileDialog';
import FileTreeTitleRenderer from './fileTreeTitleRenderer';
import ShareDialog from './shareDialog';
import UploadDialog from './uploadDialog';
import { useTranslation } from 'react-i18next';
interface FileManagerProps {
  hiddenMenu?: boolean;
  directorySelectHandler?: (path: string) => void;
  onDropToLLMManager?: (items: LLMDropItem[]) => void;
  onDragOverLLMManager?: (isOver: boolean) => void;
}

const FileManager = ({ hiddenMenu, directorySelectHandler, onDropToLLMManager, onDragOverLLMManager }: FileManagerProps) => {
  const { message, modal } = App.useApp();

  const fileTree = useFileStore((state) => state.fileTree);
  const [treeRefreshToken, setTreeRefreshToken] = useState(0);
  const { t } = useTranslation('common');

  // 多根工作区：根切换器与默认目录（活动根）
  const roots = useFileStore((state) => state.roots);
  const activeRootId = useFileStore((state) => state.activeRootId);
  const switchRoot = useFileStore((state) => state.switchRoot);
  const defaultDirFor = useFileStore((state) => state.baseHome);
  const activeRoot = roots.find((r) => r.id === activeRootId);

  useEffect(() => {
    setTreeRefreshToken((n) => n + 1);
  }, [fileTree]);

  const clickTimer = useRef<NodeJS.Timeout | null>(null);
  const clickCount = useRef(0);
  const folderClickTimer = useRef<NodeJS.Timeout | null>(null);
  const suppressClickAfterDragTimer = useRef<NodeJS.Timeout | null>(null);
  const suppressClickAfterDragRef = useRef(false);
  // const longPressTimer = useRef<NodeJS.Timeout | null>(null);
  // const isLongPress = useRef(false);
  const lastClickedKey = useRef<string>('');

  // dnd-kit state
  const [activeId, setActiveId] = useState<string | null>(null);
  const homeDropId = '__home-drop__';
  const { setNodeRef: setHomeDropRef } = useDroppable({
    id: homeDropId,
  });
  const emptyAreaRef = useRef<HTMLDivElement | null>(null);
  const dragCenterRef = useRef<{ x: number; y: number } | null>(null);
  const pointerPositionRef = useRef<{ x: number; y: number } | null>(null);
  const isOverLLMManagerRef = useRef(false);
  const autoScrollRafRef = useRef<number | null>(null);
  const llmDropZoneSelector = '[data-drop-zone="llm-manager"]';
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const startSuppressClickAfterDrag = () => {
    suppressClickAfterDragRef.current = true;
    if (suppressClickAfterDragTimer.current) {
      clearTimeout(suppressClickAfterDragTimer.current);
    }
    suppressClickAfterDragTimer.current = setTimeout(() => {
      suppressClickAfterDragRef.current = false;
      suppressClickAfterDragTimer.current = null;
    }, 180);
  };

  const folderClickHandler = async (path: string) => {
    // 如果正在拖动，不执行展开/折叠操作
    if (activeId) {
      return;
    }
    if (expandedKeys.includes(path)) {
      closeFolder(path);
    } else {
      openFolder(path);
    }
  }

  const setActiveFolderPath = useFileStore((state) => state.setActiveFolderPath);
  const openFolder = useFileStore((state) => state.openFolder);
  const closeFolder = useFileStore((state) => state.closeFolder);

  const expandedKeys = useFileStore((state) => state.expandedKeys);
  const selectedKeys = useFileStore((state) => state.selectedKeys);

  const openFile = useFileStore((state) => state.openFile);
  const isFileNew = useFileStore((state) => state.isFileNew); // 新增：获取检查文件是否为新创建的方法
  const doesFolderContainNewFiles = useFileStore((state) => state.doesFolderContainNewFiles); // 新增：获取检查文件夹是否包含新文件的方法
  const markFileAsNew = useFileStore((state) => state.markFileAsNew); // 新增：获取标记文件为新创建的方法

  const renamedKey = useFileStore((state) => state.renamedKey);

  const setSelectedKeys = useFileStore((state) => state.setSelectedKeys);

  const initializeTreedata = useFileStore((state) => state.initializeTreedata);
  const includeHidden = useFileStore((state) => state.includeHidden);
  const setIncludeHidden = useFileStore((state) => state.setIncludeHidden);
  const onlyShowShared = useFileStore((state) => state.onlyShowShared);
  const setOnlyShowShared = useFileStore((state) => state.setOnlyShowShared);

  const isMultipleSelect = useRef<'multiple' | 'single' | undefined>(undefined);

  const currentHoverPath = useRef<string>('');

  // Get user permissions
  const permissions = usePermissionsStore((state) => state.permissions);

  // shortcut key
  useKeyPress('shift', () => {
    if (!isMultipleSelect.current) {
      isMultipleSelect.current = 'multiple';
      console.log("Multiple select mode enabled:", "shift ", isMultipleSelect.current);
    }
  }, { events: ['keydown'] })
  useKeyPress('shift', () => {
    if (isMultipleSelect.current) {
      isMultipleSelect.current = undefined;
      console.log("Multiple select mode disabled:", "shift ", isMultipleSelect.current);
    }
  }, { events: ['keyup'] })

  const isMac = /Mac|iPod|iPhone|iPad/.test(navigator.platform);
  useKeyPress(isMac ? 'meta' : 'ctrl', () => {
    if (!isMultipleSelect.current) {
      isMultipleSelect.current = 'single';
      console.log("Single select mode enabled:", isMac ? "meta" : "ctrl", isMultipleSelect.current);
    }
  }, { events: ['keydown'] })
  useKeyPress(isMac ? 'meta' : 'ctrl', () => {
    if (isMultipleSelect.current) {
      isMultipleSelect.current = undefined;
      console.log("Single select mode disabled:", isMac ? "meta" : "ctrl", isMultipleSelect.current);
    }
  }, { events: ['keyup'] })


  // 添加全局键盘事件监听作为备用
  React.useEffect(() => {
    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Shift') {
        isMultipleSelect.current = undefined;
      }
      if (e.key === (isMac ? 'Meta' : 'Control')) {
        isMultipleSelect.current = undefined;
      }
    };

    const handleBlur = () => {
      isMultipleSelect.current = undefined;
    };

    // 添加全局文件夹图标点击事件监听
    const handleFolderIconClick = (e: MouseEvent) => {
      // 如果正在拖动，不执行展开/折叠操作
      if (activeId) {
        return;
      }

      let target = e.target as HTMLElement;

      // 向上遍历 DOM 树查找带有目标 ID 的元素（最多向上查找 5 层）
      let currentElement: HTMLElement | null = target;
      let elementWithId: HTMLElement | null = null;
      let depth = 0;
      const maxDepth = 5;

      while (currentElement && depth < maxDepth) {
        if (currentElement.id &&
          (currentElement.id.startsWith('folder-open-') || currentElement.id.startsWith('folder-close-'))) {
          elementWithId = currentElement;
          break;
        }
        currentElement = currentElement.parentElement;
        depth++;
      }

      // 如果找到了带有目标 ID 的元素
      if (elementWithId && elementWithId.id) {
        const targetId = elementWithId.id;
        console.log("targetId:", targetId)
        // 检查是否是文件夹图标
        if (targetId.startsWith('folder-open-') || targetId.startsWith('folder-close-')) {
          e.preventDefault();
          e.stopPropagation();

          // 从 id 中提取路径信息
          const pathMatch = targetId.match(/^folder-(open|close)-(.+)$/);
          if (pathMatch) {
            const folderPath = pathMatch[2];
            console.log('Folder icon clicked:', targetId, 'Path:', folderPath);

            if (targetId.includes("folder-open-")) {
              closeFolder(folderPath);
            } else {
              openFolder(folderPath);
            }

          }
        }
      }
    };

    window.addEventListener('keyup', handleKeyUp);
    window.addEventListener('blur', handleBlur);
    window.addEventListener('click', handleFolderIconClick);

    return () => {
      window.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('blur', handleBlur);
      window.removeEventListener('click', handleFolderIconClick);

      // Cleanup timers
      if (clickTimer.current) {
        clearTimeout(clickTimer.current);
      }
      if (folderClickTimer.current) {
        clearTimeout(folderClickTimer.current);
      }
      // if (longPressTimer.current) {
      //   clearTimeout(longPressTimer.current);
      // }
    };
  }, [isMac, activeId]);

  useEffect(() => {
    return () => {
      if (suppressClickAfterDragTimer.current) {
        clearTimeout(suppressClickAfterDragTimer.current);
        suppressClickAfterDragTimer.current = null;
      }
      suppressClickAfterDragRef.current = false;
    };
  }, []);

  const wrapperRef = useRef<HTMLDivElement>(null);

  useClickAway(() => {
    setSelectedKeys([])
  }, wrapperRef);

  // Add a ref for the tree component
  const treeRef = useRef<any>(null);

  // Scroll to the selected node when selectedKeys changes
  useUpdateEffect(() => {
    if (selectedKeys.length > 0) {
      scrollToSelectedNode(selectedKeys[0]);
    }
  }, [selectedKeys]);

  // Function to scroll to a selected node
  const scrollToSelectedNode = (key: string) => {
    setTimeout(() => {
      const treeWrapper = document.getElementById('tree-wrapper');
      if (!treeWrapper) return;

      const selectedElement = treeWrapper.querySelector(`[data-key="${key}"]`)
      if (selectedElement) {
        // 检查元素是否已经在视口中
        const rect = selectedElement.getBoundingClientRect();
        const wrapperRect = treeWrapper.getBoundingClientRect();

        // 判断元素是否完全在可视区域内
        const isVisible = (
          rect.top >= wrapperRect.top &&
          rect.bottom <= wrapperRect.bottom
        );

        // 只有当元素不可见时才滚动
        if (!isVisible) {
          selectedElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    }, 300); // Small delay to ensure the DOM is updated
  };


  useKeyPress(isMac ? ['meta.c'] : ['ctrl.c'], (e) => {
    console.log("copy:", selectedKeys)
    if (selectedKeys.length) {
      e.preventDefault();
      setCopiedFilePathList(selectedKeys as string[]);
    }
  }, {
    exactMatch: true
  });


  useKeyPress(isMac ? ['meta.v'] : ['ctrl.v'], (e) => {
    if (selectedKeys?.[0] && currentHoverPath.current) {
      e.preventDefault();
      handlePasteFile(currentHoverPath.current);
    }
  }, {
    exactMatch: true
  });

  const flatTreeData = (data: FileInfoExtended): FileInfoExtended[] => {
    return data?.children.reduce((acc, item) => {

      acc.push(item);
      if (item.children) {

        acc.push(...flatTreeData(item));
      }
      return acc;
    }, [] as FileInfoExtended[]) ?? [];
  }

  const multipleSelectHandler = (selectedKey: string, type: 'multiple' | 'single') => {
    if (!fileTree) return;
    let keys = [selectedKey];
    if (type === 'multiple') {
      if (selectedKeys.includes(selectedKey)) {
        keys = selectedKeys.filter(key => key !== selectedKey);
        setSelectedKeys(keys);
        return;
      }

      if (selectedKeys.length >= 1) {
        const dataList = flatTreeData(fileTree);
        const currIndex = dataList.findIndex(node => node.key === selectedKey);
        const lastIndex = dataList.findIndex(node => node.key === selectedKeys[0]);
        const range = [currIndex, lastIndex].sort((a, b) => a - b);
        const newSelectedKeys = dataList.slice(range[0], range[1] + 1).map(node => node.key);
        keys = [...new Set([...selectedKeys, ...newSelectedKeys])];
      }

    } else if (type === 'single') {
      if (selectedKeys.includes(selectedKey)) {
        keys = selectedKeys?.filter(key => key !== selectedKey);
      } else {
        keys = [...new Set([...selectedKeys, ...keys])];
      }

    }
    setSelectedKeys(keys);
  }

  const cancelMultipleSelect = () => {
    setSelectedKeys([]);
  }

  // Unified click handler: selection/open/expand/rename are coordinated here.
  const handleItemMouseUp = (selectedPath: string, info: any) => {

    if (renamedKey === selectedPath) {
      return;
    }

    const isClickedItemInMultiSelection = selectedKeys.length > 1 && selectedKeys.includes(selectedPath);

    // Ignore click right after drag end/cancel to prevent accidental open/expand.
    if (activeId || suppressClickAfterDragRef.current) {
      return;
    }

    if (isMultipleSelect.current) {
      multipleSelectHandler(selectedPath, isMultipleSelect.current);
      if (selectedKeys.length === 0) {
        if ((info.node as any).isDir) {
          setActiveFolderPath(selectedPath as string);
        } else {
          openFile(selectedPath);
        }
      }

      return;
    }

    setSelectedKeys([selectedPath]);

    if ((info.node as any).isDir) {
      setActiveFolderPath(selectedPath as string);
      directorySelectHandler?.(selectedPath as string);
      // Clicking an item from multi-selection should collapse selection and open the target folder.
      if (isClickedItemInMultiSelection) {
        openFolder(selectedPath);
      } else {
        folderClickHandler(selectedPath);
      }
      return;
    }

    if ((info.node as any).type == FileType.BLOB && (info.node as any).extension !== FileExtension.SMILE && !(isWordFile(selectedPath) || isExcelFile(selectedPath))) {
      modal.info({
        title: t('file_manager.blob_file'),
        content: t('file_manager.blob_file_desc'),
        onOk() {
          openFile(selectedPath as string);
        },
        onCancel() { },
        okCancel: true,
      })
      return;
    }

    openFile(selectedPath as string);

    if (lastClickedKey.current === selectedPath) {
      clickCount.current += 1;
    } else {
      clickCount.current = 1;
      lastClickedKey.current = selectedPath;
    }

    // Clear existing timer
    if (clickTimer.current) {
      clearTimeout(clickTimer.current);
    }

    // Set timer for double click detection
    clickTimer.current = setTimeout(() => {
      // Check if it's a double click when timer expires
      if (clickCount.current >= 2) {
        setRenamedKey(selectedPath)
      }

      // Reset states
      clickCount.current = 0;
      lastClickedKey.current = '';
    }, 300);
  };

  const moveFile = useFileStore((state) => state.moveFile);

  const updateLLMDragState = (isOver: boolean) => {
    if (isOverLLMManagerRef.current === isOver) {
      return;
    }
    isOverLLMManagerRef.current = isOver;
    onDragOverLLMManager?.(isOver);
  };

  const isPointOverLLMManager = (x: number, y: number) => {
    const llmDropZone = document.querySelector(llmDropZoneSelector) as HTMLElement | null;
    if (llmDropZone) {
      const rect = llmDropZone.getBoundingClientRect();
      const insideRect = x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
      if (insideRect) {
        return true;
      }
    }
    const elementAtPoint = document.elementFromPoint(x, y);
    return !!elementAtPoint?.closest(llmDropZoneSelector);
  };

  const getCurrentDragPoint = () => {
    if (pointerPositionRef.current) {
      return pointerPositionRef.current;
    }
    return dragCenterRef.current;
  };

  useEffect(() => {
    if (!activeId) {
      pointerPositionRef.current = null;
      return;
    }

    const handlePointerMove = (event: PointerEvent) => {
      pointerPositionRef.current = { x: event.clientX, y: event.clientY };
      updateLLMDragState(isPointOverLLMManager(event.clientX, event.clientY));
    };

    window.addEventListener('pointermove', handlePointerMove);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      pointerPositionRef.current = null;
    };
  }, [activeId]);

  // dnd-kit handlers
  const performAutoScroll = (pointerX: number, pointerY: number) => {
    const treeWrapper = document.getElementById('tree-wrapper');
    if (!treeWrapper) return;

    const rect = treeWrapper.getBoundingClientRect();
    const threshold = 50;
    const speed = 10;

    let scrollY = 0;
    if (pointerY < rect.top + threshold) {
      scrollY = -speed;
    } else if (pointerY > rect.bottom - threshold) {
      scrollY = speed;
    }

    let scrollX = 0;
    if (pointerX < rect.left + threshold) {
      scrollX = -speed;
    } else if (pointerX > rect.right - threshold) {
      scrollX = speed;
    }

    if (scrollY !== 0 || scrollX !== 0) {
      treeWrapper.scrollTop += scrollY;
      treeWrapper.scrollLeft += scrollX;
    }
  };

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
    dragCenterRef.current = null;
    const activatorEvent = event.activatorEvent;
    if (activatorEvent instanceof PointerEvent) {
      pointerPositionRef.current = { x: activatorEvent.clientX, y: activatorEvent.clientY };
    }
    updateLLMDragState(false);
  };

  const handleDragMove = (event: DragMoveEvent) => {
    const initialRect = event.active.rect.current.initial;
    if (initialRect) {
      const centerX = initialRect.left + initialRect.width / 2 + event.delta.x;
      const centerY = initialRect.top + initialRect.height / 2 + event.delta.y;
      dragCenterRef.current = { x: centerX, y: centerY };
      const point = getCurrentDragPoint();
      if (point) {
        updateLLMDragState(isPointOverLLMManager(point.x, point.y));
        if (autoScrollRafRef.current) {
          cancelAnimationFrame(autoScrollRafRef.current);
        }
        autoScrollRafRef.current = requestAnimationFrame(() => {
          performAutoScroll(point.x, point.y);
        });
      }
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    startSuppressClickAfterDrag();

    setActiveId(null);
    try {
      if (!fileTree) return;

      const dragKey = active.id as string;
      let dropKey = over?.id as string | undefined;

      const currentPoint = getCurrentDragPoint();

      if (!dropKey && currentPoint && wrapperRef.current) {
        const wrapperRect = wrapperRef.current.getBoundingClientRect();
        const { x, y } = currentPoint;
        const isInsideWrapper = x >= wrapperRect.left && x <= wrapperRect.right && y >= wrapperRect.top && y <= wrapperRect.bottom;
        if (isInsideWrapper) {
          dropKey = homeDropId;
        }
      }

      if (!dropKey) {
        const droppedOnLLMByState = isOverLLMManagerRef.current;
        const droppedOnLLMByPoint = currentPoint
          ? isPointOverLLMManager(currentPoint.x, currentPoint.y)
          : false;

        const droppedOnLLM = droppedOnLLMByState || droppedOnLLMByPoint;

        if (currentPoint && droppedOnLLM && onDropToLLMManager) {
          let dragFileList = [dragKey];

          if (selectedKeys.length > 1 && selectedKeys.includes(dragKey)) {
            dragFileList = selectedKeys as string[];
          }

          const droppedItems: LLMDropItem[] = dragFileList
            .map((path) => {
              const node = findNode(fileTree, path);
              if (!node) {
                return null;
              }
              return {
                path: node.path,
                name: node.name,
                type: node.isDir ? 'folder' : 'file',
              };
            })
            .filter((item): item is LLMDropItem => !!item);

          if (droppedItems.length > 0) {
            onDropToLLMManager(droppedItems);
          }
        }
        return;
      }

      if (dragKey === dropKey) return;

      let dragFileList = [dragKey];

      if (selectedKeys.length > 1 && selectedKeys.includes(dragKey)) {
        dragFileList = selectedKeys as string[];
      }

      let destinationPath: string | undefined = undefined;

      if (dropKey === homeDropId) {
        destinationPath = defaultDirFor;
      } else {
        const destinationNode = findNode(fileTree, dropKey);
        if (!destinationNode) {
          message.error(t('file_manager.move_failed'));
          return;
        }

        if (destinationNode?.isDir) {
          destinationPath = destinationNode.path;
        } else {
          destinationPath = destinationNode.path.split('/').slice(0, -1).join('/');
        }
      }

      if (!destinationPath) {
        return;
      }

      const filteredDragList = dragFileList.filter((path) => {
        const node = findNode(fileTree, path);
        const currentParent = node?.path ? pathbe.dirname(node.path) : pathbe.dirname(path);
        return currentParent !== destinationPath;
      });

      if (!filteredDragList.length) {
        return;
      }

      await moveFile(filteredDragList, destinationPath);
    } finally {
      if (autoScrollRafRef.current) {
        cancelAnimationFrame(autoScrollRafRef.current);
        autoScrollRafRef.current = null;
      }
      dragCenterRef.current = null;
      pointerPositionRef.current = null;
      updateLLMDragState(false);
      resetHorizontalScroll();
    }
  };

  const resetHorizontalScroll = () => {
    const treeWrapper = document.getElementById('tree-wrapper');
    if (treeWrapper) {
      treeWrapper.scrollLeft = 0;
    }
  };

  const handleDragCancel = () => {
    startSuppressClickAfterDrag();

    if (autoScrollRafRef.current) {
      cancelAnimationFrame(autoScrollRafRef.current);
      autoScrollRafRef.current = null;
    }
    setActiveId(null);
    dragCenterRef.current = null;
    updateLLMDragState(false);
    resetHorizontalScroll();
  };

  const getActiveNode = () => {
    if (!activeId || !fileTree) return null;
    return findNode(fileTree, activeId);
  };

  const onDrop: TreeProps['onDrop'] = async (info) => {
    if (!fileTree) return;

    const dropKey = info.node.key;
    const dragKey = info.dragNode.key;

    let dragFileList = [dragKey as string]

    if (selectedKeys.length > 1) {
      dragFileList = selectedKeys as string[]
    }

    const destinationNode = findNode(fileTree, (dropKey as string));
    if (!destinationNode) {
      message.error(t('file_manager.paste_failed'));
      return;
    }




    let destinationPath: string

    if (destinationNode?.isDir) {

      destinationPath = `${destinationNode?.path}`
    } else {

      destinationPath = destinationNode?.path?.split('/').slice(0, -1).join('/')
    }

    await moveFile(dragFileList as string[], destinationPath);
  };


  // right click feature
  const [copiedFilePathList, setCopiedFilePathList] = useState<string[] | null>(null);

  const [fileMenuVisibleKey, setFileMenuVisibleKey] = useState<string | undefined>();

  const generateMenuItems = () => {
    if (!fileTree) return [];
    let items: ItemType[] = []
    const node = fileMenuVisibleKey ? findNode(fileTree, fileMenuVisibleKey) : null;
    if (copiedFilePathList?.length) {
      items.push(...[{
        label: t('file_manager.paste'),
        key: 'PASTE',
      }])
    }
    if (selectedKeys.length <= 1) {

      if (!fileMenuVisibleKey) {
        items.push(...[
          {
            label: t('file_manager.upload'),
            key: 'UPLOAD',
          },
          {
            label: t('file_manager.new_folder'),
            key: 'NEW_FOLDER',
          },
          {
            label: t('file_manager.new_file'),
            key: 'NEW_FILE'
          }
        ])
      } else {
        if (node && node.isDir) {
          items.push(
            {
              label: t('file_manager.download'),
              key: 'DOWNLOAD_DIR',
            },
            {
              label: t('file_manager.rename'),
              key: 'RENAME_FOLDER',
            })
          // Add Share option if conditions are met
          if (fileMenuVisibleKey && permissions?.["share"]) {
            items.push({
              label: t('file_manager.share'),
              key: 'SHARE',
            });
          }
        }

        if (node && !node.isDir) {
          items.push(...[
            {
              label: t('file_manager.download'),
              key: 'DOWNLOAD',
            },
            {
              label: t('file_manager.rename'),
              key: 'RENAME_FILE',
            }
          ])
        }

        items.push(...[
          {
            label: t('file_manager.copy'),
            key: 'COPY',
          },
          {
            label: t('file_manager.delete'),
            key: 'DELETE',
            danger: true,
          },
          {
            label: t('file_manager.copy_absolute_path'),
            key: 'COPY_ABSOLUTE_PATH',
          }
        ])
      }


    } else {
      items.push(...[
        {
          label: t('file_manager.copy'),
          key: 'COPY_MULTIPLE',
        },
        {
          label: t('file_manager.batch_download'),
          key: 'DOWNLOAD_MULTIPLE',
        },
        {
          label: t('file_manager.delete'),
          key: 'DELETE_MULTIPLE',
          danger: true,
        }
      ])
    }
    return items
  }

  const rightClickMenuItems: MenuProps = {
    items: generateMenuItems(),
    onClick: (item) => {
      switch (item.key) {
        case 'DELETE':
          fileMenuVisibleKey && handleDeleteFile(fileMenuVisibleKey);
          break;
        case 'RENAME_FOLDER':
          fileMenuVisibleKey && handleRenameFile(fileMenuVisibleKey);
          break;
        case 'RENAME_FILE':
          fileMenuVisibleKey && handleRenameFile(fileMenuVisibleKey);
          break;
        case 'COPY':
          fileMenuVisibleKey && setCopiedFilePathList([fileMenuVisibleKey]);
          break;
        case 'PASTE':
          handlePasteFile(fileMenuVisibleKey);
          break;
        case 'UPLOAD':
          setUploadDialogVisible(true);
          break;
        case 'NEW_FOLDER':
          handleCreateDialogOpen('folder');
          break;
        case 'NEW_FILE':
          handleCreateDialogOpen('file');
          break;
        case 'DOWNLOAD_DIR':
          fileMenuVisibleKey && handleDownloadDir(fileMenuVisibleKey);
          break;
        case 'DOWNLOAD':
          fileMenuVisibleKey && handleDownloadFile(fileMenuVisibleKey);
          break;
        case 'SHARE':
          if (fileMenuVisibleKey) {
            setShareFilePath(fileMenuVisibleKey);
            setShareDialogVisible(true);
          }
          break;
        case 'DOWNLOAD_MULTIPLE':
          selectedKeys.length > 1 && handleBatchDownload(selectedKeys as string[]);
          break;
        case 'DELETE_MULTIPLE':
          selectedKeys.length > 1 && handleDeleteMultipleFiles(selectedKeys as string[]);
          break
        case 'COPY_MULTIPLE':
          selectedKeys.length > 1 && setCopiedFilePathList(selectedKeys as string[]);
          break;
        case 'COPY_ABSOLUTE_PATH':
          if (fileMenuVisibleKey) {
            if (navigator?.clipboard && window?.isSecureContext) {
              navigator.clipboard.writeText(fileMenuVisibleKey).then(() => {
                message.success(t('file_manager.path_copied'));
              });
            } else {
              unsecuredCopyToClipboard(fileMenuVisibleKey)
            }

          }
          break;
        default:
          break;
      }
    }
  }

  const deleteFile = useFileStore((state) => state.deleteFile);

  const handleDeleteFile = async (filePath: string) => {
    const fileName = filePath.split('/').pop();
    modal.confirm({
      title: t('file_manager.delete_file_title'),
      content: t('file_manager.delete_file_content', { name: fileName }),
      onOk: async () => {
        const isSuccess = await deleteFile([filePath]);
        if (isSuccess) {
          message.success(t('file_manager.delete_success'));
        }
      },
    });
  };

  const handleDeleteMultipleFiles = async (filePaths: string[]) => {
    modal.confirm({
      title: t('file_manager.delete_files_title'),
      content: t('file_manager.delete_files_content', { count: filePaths.length }),
      onOk: async () => {
        const isSuccess = await deleteFile(filePaths);
        if (isSuccess) {
          message.success(t('file_manager.delete_success'));
        }
      },
    });
  }


  const setRenamedKey = useFileStore((state) => state.setRenamedKey);

  const handleRenameFile = async (filePath: string) => {
    setRenamedKey(filePath);

    // setTimeout(() => {
    //   const inputElement = document.getElementById(`rename-${filePath}`);
    //   inputElement?.focus();
    // },300)

  };

  const handlePasteFile = async (fileMenuVisibleKey?: string) => {
    if (!copiedFilePathList?.length || !fileTree) return;

    let destinationPath: string | undefined = undefined

    let destinationNode: FileInfoExtended | undefined = undefined;
    if (fileMenuVisibleKey) {
      destinationNode = findNode(fileTree, fileMenuVisibleKey);

      destinationPath = destinationNode?.path;
    } else {
      destinationPath = defaultDirFor;
    }

    if (!destinationPath) {
      message.error(t('file_manager.destination_empty'));
      return;
    }

    let targetIsDir = false;
    if (destinationNode) {

      targetIsDir = destinationNode.isDir;
    }

    // home directory is always a directory
    if (!fileMenuVisibleKey) {
      targetIsDir = true;
    }

    const params: { src: string, dst: string, isDir: boolean, force?: boolean }[] = [];

    copiedFilePathList.forEach((copiedFilePath) => {
      const fileName = copiedFilePath.split('/').pop();
      let dst: string

      if (targetIsDir) {

        dst = `${destinationPath}/${fileName}`
      } else {

        dst = destinationPath?.split('/').slice(0, -1).join('/') + `/${fileName}`
      }

      let srcIsDir = false;
      const copiedNode = findNode(fileTree, copiedFilePath);
      if (copiedNode) {

        srcIsDir = copiedNode.isDir;
      }

      params.push({ src: copiedFilePath, dst: dst, isDir: srcIsDir });
    })



    const res = await fileApi.copyFileResource(params);

    if (res?.ok) {
      if (res.message !== "") {
        const messageList = res.message.split('\n');
        messageList.forEach((msg) => msg !== "" && message.warning(msg));
      }
      setCopiedFilePathList(null); // Clear the copied file path after pasting
    } else {
      if (res.message === "409") {
        const conflictPaths = res.data
        const renameMap = new Map<string, string>();

        modal.confirm({
          title: 'File or folder conflicts detected',
          width: 700,
          content: (
            <div className="space-y-4">
              <p className="text-gray-600 dark:text-[#999999]">
                The following files or folders already exist in the destination. Choose an action for each:
              </p>
              <div className="max-h-80 overflow-y-auto">
                <div className="space-y-2">
                  {conflictPaths && Object.keys(conflictPaths).map((path: string, index: number) => {
                    let defaultValue = conflictPaths[path];


                    if (!renameMap.has(path) && defaultValue) {
                      renameMap.set(path, defaultValue);
                    }

                    return (
                      <div key={index} className="border border-gray-300 dark:border-[#404040] p-2 rounded bg-white dark:bg-[#252526]">
                        <div className="mb-1">
                          <span className="text-xs text-gray-500 dark:text-[#999999] font-mono break-all">
                            {path}
                          </span>
                        </div>
                        <div className="flex items-center space-x-4">
                          <div className="flex items-center space-x-1">
                            <input
                              type="radio"
                              id={`overwrite-${index}`}
                              name={`action-${index}`}
                              value="overwrite"
                              defaultChecked
                              className="text-blue-600 dark:text-[#3794DE]"
                            />
                            <label htmlFor={`overwrite-${index}`} className="text-xs text-gray-700 dark:text-[#CCCCCC]">
                              Overwrite
                            </label>
                          </div>
                          <div className="flex items-center space-x-1">
                            <input
                              type="radio"
                              id={`rename-${index}`}
                              name={`action-${index}`}
                              value="rename"
                              className="text-blue-600 dark:text-[#3794DE]"
                            />
                            <label htmlFor={`rename-${index}`} className="text-xs text-gray-700 dark:text-[#CCCCCC]">
                              Rename to:
                            </label>
                          </div>
                          <Input
                            id={`rename-input-${index}`}
                            placeholder="Enter new name"
                            defaultValue={defaultValue}
                            size="small"
                            style={{ width: 200 }}
                            onChange={(e) => renameMap.set(path, e.target.value)}
                            onFocus={() => {
                              // Auto select rename option when input is focused
                              const renameRadio = document.getElementById(`rename-${index}`) as HTMLInputElement;
                              if (renameRadio) renameRadio.checked = true;
                            }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          ),
          okText: 'Confirm',
          cancelText: 'Cancel',
          onOk: async () => {
            const newParams: typeof params = [];
            const conflictPathList = conflictPaths ? Array.from(Object.keys(conflictPaths)) : [];
            for (let i = 0; i < conflictPathList.length; i++) {
              const path = conflictPathList[i];
              const overwriteRadio = document.getElementById(`overwrite-${i}`) as HTMLInputElement;
              const renameRadio = document.getElementById(`rename-${i}`) as HTMLInputElement;

              const correspondingParam = params.find(p => p.src === path);
              if (!correspondingParam) continue;

              if (renameRadio && renameRadio.checked) {
                const newName = renameMap.get(path);
                if (!newName || !newName.trim()) {
                  message.error(`Please enter a new name for ${path.split('/').pop()}`);
                  return Promise.reject();
                }

                const renamedParam = {
                  ...correspondingParam,
                  dst: pathbe.join(pathbe.dirname(correspondingParam.dst), newName.trim())
                };
                newParams.push(renamedParam);
              } else if (overwriteRadio && overwriteRadio.checked) {
                const overwriteParam = {
                  ...correspondingParam,
                  force: true,
                }
                newParams.push(overwriteParam);
              }
            }

            // Add non-conflicting files to the overwrite list (they don't need special handling)
            const nonConflictingFiles = params.filter(p => !conflictPathList.includes(p.src));
            newParams.push(...nonConflictingFiles);
            console.log("Final params for copying:", newParams);

            try {
              // Handle rename files first
              const res = await fileApi.copyFileResource(newParams);
              if (!res?.ok) {
                if (res?.message === "-1") {
                  message.error(t('file_manager.conflict_retry'));
                  return Promise.reject();
                }
                message.error(res?.message || t('file_manager.process_failed'));
                return Promise.reject();
              }
              setCopiedFilePathList(null);
              message.success(t('file_manager.process_success'));
            } catch (error) {
              console.error('Error processing files:', error);
              message.error(t('file_manager.process_failed'));
              return Promise.reject();
            }
          },
          onCancel: () => {
            renameMap.clear();
          }
        });
        return
      } else {
        message.error(res.message);
      }
    }

  };

  const [uploadDialogVisible, setUploadDialogVisible] = useState(false);

  const [createDialogVisible, setCreateDialogVisible] = useState<'folder' | 'file' | undefined>(undefined);

  const [shareDialogVisible, setShareDialogVisible] = useState(false);
  const [shareFilePath, setShareFilePath] = useState<string>('');

  const [sharedPaths, setSharedPaths] = useState<Set<string>>(new Set());
  const [shareInfoMap, setShareInfoMap] = useState<Map<string, SharedWithMeEntry>>(new Map());

  useEffect(() => {
    const fetchSharedWithMe = async () => {
      try {
        const res = await shareApi.getShareWithMe();
        if (res?.ok && res.data) {
          const paths = new Set<string>();
          const infoMap = new Map<string, SharedWithMeEntry>();
          for (const entry of res.data) {
            if (entry.status !== 'pending' && entry.symbol_link_path) {
              paths.add(entry.symbol_link_path);
              infoMap.set(entry.symbol_link_path, entry);
            }
          }
          setSharedPaths(paths);
          setShareInfoMap(infoMap);
        }
      } catch (e) {
        console.error('Failed to fetch shared-with-me:', e);
      }
    };
    fetchSharedWithMe();
  }, [fileTree]);


  const handleCreateDialogOpen = (type: 'folder' | 'file') => {
    setCreateDialogVisible(type);
  }

  const handleCreateDialogClose = () => {
    setCreateDialogVisible(undefined);
  }

  const handleCreateSuccess = () => {
    setCreateDialogVisible(undefined);
  }


  const handleDownloadDir = async (filePath: string) => {
    modal.confirm({
      title: 'Download Directory',
      content: `Are you sure you want to download ${filePath.split('/').pop()} as a zip file?`,
      onOk: async () => {
        try {
          const content = await fileApi.getRawFileResourceInBlob(filePath + "?algo=zip");
          if (content) {
            const url = window.URL.createObjectURL(content);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${filePath.split('/').pop()}.zip`);
            document.body.appendChild(link);
            link.click();
          } else {
            message.error(t('file_manager.download_dir_failed'));
          }
        } catch (e) {
          message.error(t('file_manager.download_dir_failed'));
        }
      }
    });
  }

  const handleBatchDownload = async (filePaths: string[]) => {
    try {
      const blob = await fileApi.batchDownloadResource(filePaths, 'download');
      if (!blob) throw new Error('empty blob');
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'download.zip';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      message.error(t('file_manager.download_file_failed'));
    }
  };

  const handleDownloadFile = async (filePath: string) => {
    try {
      const blob = await fileApi.getRawFileResourceInBlob(filePath);
      if (!blob) throw new Error('empty blob');

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filePath.split('/').pop() ?? '';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      message.error(t('file_manager.download_file_failed'));
    }
  };
  const handleDrop = async (event: React.DragEvent<HTMLSpanElement>, node?: FileInfo & TreeDataNode) => {


    const droppedFiles = Array.from(event.dataTransfer.files);
    if (droppedFiles?.length) {
      event.preventDefault();
      event.stopPropagation();
      for (let i = 0; i < droppedFiles.length; i++) {
        const file = droppedFiles[i]
        const isFile = await isThisAFile(file)
        if (!isFile) {
          message.error(t('file_manager.invalid_file_type'));
          return;
        }
      }

      let targetDir: string | undefined

      if (node) {
        if (node.isDir) {
          targetDir = node.path
        } else {
          targetDir = node.path.split('/').slice(0, -1).join('/')
        }
      } else {
        targetDir = defaultDirFor
      }



      if (!targetDir) {
        message.error(t('file_manager.upload_failed'));
        return;
      }

      let contentPlaceHolder = '';

      if (droppedFiles.length === 1) {
        contentPlaceHolder = droppedFiles[0].name
      } else {
        contentPlaceHolder = `${droppedFiles.length} files/directory`
      }

      const formatFiles = droppedFiles.map(file => {
        const uid = uuidv4();
        return {
          uid,
          name: file.name,
          originFileObj: file as UploadFile['originFileObj'],
        }
      })

      modal.confirm({
        title: 'Upload Files',
        content: `Are you sure you want to upload ${contentPlaceHolder} into ${targetDir === defaultDirFor ? (activeRoot?.name ?? 'home') : targetDir}?`,
        onOk: async () => {
          let res
          try {
            res = await fileService.uploadFiles(formatFiles, targetDir)
          } catch (e) {
            message.error(t('file_manager.upload_failed'));
            console.log(e)
          }
          if (res?.ok) {
            if (res.data?.hasConflicts) {
              modal.confirm({
                title: 'File already exists',
                content: `${res.data?.conflictFiles?.join(",") || "File"} already exists, do you want to overwrite it?`,
                okText: 'Overwrite',
                cancelText: 'Cancel',
                onOk: async () => {
                  try {
                    const reUploadFiles = formatFiles.filter(file => res.data?.conflictFiles?.includes(file.name));
                    const overrideRes = await fileService.uploadFiles(reUploadFiles, targetDir, true)
                    if (overrideRes?.ok) {
                      message.success(t('file_manager.upload_success'));

                      // 标记覆盖上传成功的文件为新文件
                      if (overrideRes.data?.successFiles) {
                        overrideRes.data.successFiles.forEach(file => {
                          markFileAsNew(file.path);
                        });
                      }
                    }
                    if (overrideRes?.message) {
                      message.error(overrideRes?.message);
                    }
                  } catch (e) {
                    message.error(t('file_manager.upload_failed'));
                    console.log(e)
                  }
                }
              });
            } else {
              message.success(t('file_manager.upload_success'));

              // 标记上传成功的文件为新文件
              if (res.data?.successFiles) {
                res.data.successFiles.forEach(file => {
                  markFileAsNew(file.path);
                });
              }
            }
            return
          } else {
            message.error(res?.message ?? t('file_manager.upload_failed'));
          }
        }
      })
    }

  }

  const handleDragOverInTree = (event: React.DragEvent<HTMLSpanElement>, _node: TreeDataNode) => {
    event.preventDefault();
    event.stopPropagation();
  };

  return (
    <div className='file-manager-wrapper h-full flex flex-col overflow-y-hidden bg-neutral-light dark:bg-[#1E1E1E]' style={{ overflowX: 'clip' }}>
      <div
        className="flex flex-col mx-2 my-2 rounded-md border border-black/[0.06] bg-neutral-300 dark:border-white/10 dark:bg-[#252526]"
      >

        {/* Header */}
        <div
          className="flex items-center justify-between h-8 px-2.5 border-b border-black/[0.06] bg-gradient-to-b from-black/[0.03] to-transparent dark:border-white/[0.06] dark:from-white/[0.05]"
        >

          <span className="flex min-w-0 items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-300">
            <span className="h-3 w-0.5 shrink-0 rounded-full bg-blue-500/80 dark:bg-blue-400/80" />
            <span className="truncate">{t('file_manager.explorer')}</span>
          </span>


          <div className="flex items-center">

            <Tooltip title={t('file_manager.refresh')}>
              <Button
                type="text"
                size="small"
                icon={<ReloadOutlined />}
                onClick={() => initializeTreedata()}
              />
            </Tooltip>


            <Tooltip title={t('file_manager.new_folder')}>
              <Button
                type="text"
                size="small"
                icon={<FolderAddOutlined />}
                onClick={() => handleCreateDialogOpen('folder')}
              />
            </Tooltip>

          </div>

        </div>


        {/* Filters */}
        <div
          className="flex flex-wrap items-center gap-1 px-1 py-1.5 bg-black/[0.03] dark:bg-black/20"
        >

          {roots.length > 1 && (
            <Select
              size="small"
              value={activeRootId}
              onChange={(id: string) => { void switchRoot(id); }}
              popupMatchSelectWidth={false}
              style={{ maxWidth: 190, fontSize: 11 }}
              options={roots.map((r) => ({
                value: r.id,
                label: (
                  <span title={r.path} className="inline-flex items-center gap-1">
                    {r.kind === 'home' ? <HomeOutlined /> : <FolderOpenOutlined />}
                    <span className="truncate">{r.name}</span>
                    {r.status === 'missing' && <span className="text-amber-500">⚠</span>}
                  </span>
                ),
                disabled: r.status === 'missing',
              }))}
            />
          )}

          <Checkbox
            className={`flex items-center rounded px-1.5 py-0.5 transition active:scale-95 ${includeHidden ? 'bg-blue-500/10 text-blue-600 dark:bg-blue-400/15 dark:text-blue-300' : 'text-neutral-500 hover:bg-black/[0.06] hover:text-neutral-700 dark:text-neutral-400 dark:hover:bg-white/10 dark:hover:text-neutral-200'}`}
            checked={includeHidden}
            onChange={(e) => {
              void setIncludeHidden(e.target.checked);
            }}
          >
            <span className="whitespace-nowrap text-[11px] leading-none">
              {t('file_manager.show_hidden')}
            </span>
          </Checkbox>


          <Checkbox
            className={`flex items-center rounded px-1.5 py-0.5 transition active:scale-95 ${onlyShowShared ? 'bg-blue-500/10 text-blue-600 dark:bg-blue-400/15 dark:text-blue-300' : 'text-neutral-500 hover:bg-black/[0.06] hover:text-neutral-700 dark:text-neutral-400 dark:hover:bg-white/10 dark:hover:text-neutral-200'}`}
            checked={onlyShowShared}
            onChange={(e) => {
              void setOnlyShowShared(e.target.checked);
            }}
          >
            <span className="whitespace-nowrap text-[11px] leading-none">
              {t('file_manager.only_show_shared')}
            </span>
          </Checkbox>

        </div>

      </div>

      <UploadDialog targetDir={fileMenuVisibleKey ?? defaultDirFor} visible={uploadDialogVisible} onClose={() => setUploadDialogVisible(false)} onSubmit={fileService.uploadFiles} />
      <CreateFileDialog
        visible={!!createDialogVisible}
        type={createDialogVisible}
        targetPath={fileMenuVisibleKey ?? defaultDirFor ?? ''}
        onCancel={handleCreateDialogClose}
        onSuccess={handleCreateSuccess}
      />
      <ShareDialog
        visible={shareDialogVisible}
        filePath={shareFilePath}
        onClose={() => {
          setShareDialogVisible(false);
          setShareFilePath('');
        }}
        onSuccess={() => {
          // Optionally refresh file tree or show success message
        }}
      />
      <Dropdown disabled={hiddenMenu} menu={rightClickMenuItems} className='h-full' trigger={['contextMenu']}>
        <div
          ref={wrapperRef}
          id="tree-wrapper"
          className='flex flex-col flex-grow overflow-y-auto select-none'
        // style={{ overflowX: 'clip', overscrollBehavior: 'none' }}
        >
          <DndContext
            sensors={sensors}
            autoScroll={false}
            onDragStart={handleDragStart}
            onDragMove={handleDragMove}
            onDragEnd={handleDragEnd}
            onDragCancel={handleDragCancel}
            collisionDetection={pointerWithin}
          >
            <Tree
              key={treeRefreshToken}
              ref={treeRef}
              showIcon={true}
              treeData={fileTree?.children}
              draggable={false}
              fieldNames={{ title: 'name', children: 'children', key: 'path' }}
              blockNode={true}
              virtual={false}
              switcherIcon={<></>}
              multiple={true}
              titleRender={(node) => (
                <FileTreeTitleRenderer
                  node={node}
                  onHover={(selectedPath: string) => currentHoverPath.current = selectedPath}
                  onMouseUp={(selectedPath: string) => handleItemMouseUp(selectedPath, { node })}
                  selectedKeys={selectedKeys}
                  expandedKeys={expandedKeys}
                  openFolder={openFolder}
                  isFileNew={isFileNew}
                  doesFolderContainNewFiles={doesFolderContainNewFiles}
                  setFileMenuVisibleKey={setFileMenuVisibleKey}
                  handleCreateDialogOpen={handleCreateDialogOpen}
                  setUploadDialogVisible={setUploadDialogVisible}
                  handleDrop={handleDrop}
                  handleDragOverInTree={handleDragOverInTree}
                  sharedPaths={sharedPaths}
                  shareInfoMap={shareInfoMap}
                />
              )}
              selectedKeys={selectedKeys}
              expandedKeys={expandedKeys}
              // onExpand={handleExpand}
              // onSelect={handleSelect}
              onRightClick={({ node }) => setFileMenuVisibleKey(node.key)}
              onDrop={onDrop}
              classNames={{
                root: 'dark:bg-[#1E1E1E]'
              }}
            />
            <DragOverlay>
              {activeId ? (
                <div className="px-2 py-1 bg-blue-500 dark:bg-[#0E639C] text-white rounded shadow-lg">
                  {selectedKeys.length > 1 && selectedKeys.includes(activeId)
                    ? `${selectedKeys.length} items`
                    : getActiveNode()?.name || activeId}
                </div>
              ) : null}
            </DragOverlay>
            <div
              ref={(node) => {
                setHomeDropRef(node);
                emptyAreaRef.current = node;
              }}
              onClick={() => cancelMultipleSelect()}
              className='flex-grow'
              id="empty-area"
              onContextMenu={() => setFileMenuVisibleKey(undefined)}
              onDrop={(e) => handleDrop(e)}
              onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
              style={{ minHeight: '100%' }}
            >
              <div className='w-full h-full' />
            </div>
          </DndContext>
        </div>
      </Dropdown >
    </div>
  );
};

export default FileManager;